import React, { useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline } from '@atlaskit/primitives/compiled';
import { cx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIconRender from '@atlaskit/icon/core/chevron-up';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
import Tooltip from '@atlaskit/tooltip';

import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { usePageContentDimensions } from '@confluence/object-comments/entry-points/usePageContentDimensions.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { VIEW_OMNIBAR_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useShouldHideOmniBar } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';

import type { OmniBarProps } from './omniBarTypes.ts';
import { OmniBarReactions } from './OmnibarReactions.tsx';
import { useOmnibarEligibilityFromContext } from './OmnibarEligibilityContext.tsx';
import { useOmnibarReactionsData } from './hooks/useOmnibarReactionsData.ts';
import { useOmnibarCommentTrigger } from './hooks/useOmnibarCommentTrigger.ts';

const i18n = defineMessages({
	hideOmniBar: {
		id: 'experiment-omnibar.omni-bar.hide-button',
		defaultMessage: 'Hide toolbar',
		description: 'Label for the hide OmniBar button',
	},
	pinOmniBar: {
		id: 'experiment-omnibar.omni-bar.pin-button',
		defaultMessage: 'Pin toolbar',
		description: 'Label for the pin OmniBar button',
	},
	comment: {
		id: 'experiment-omnibar.comments.comment-button',
		defaultMessage: 'Comment',
		description: 'Label for the comment button in the OmniBar',
	},
});

const styles = cssMap({
	responsiveContainer: {
		position: 'fixed',
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		height: '72px',
		bottom: token('space.0'),
		paddingBottom: token('space.300'),
		zIndex: 1,
		overflow: 'visible',
	},
	barContainerBase: {
		width: 'fit-content',
		pointerEvents: 'auto',
		opacity: 1,
		transform: 'translateY(0)',
		transition: 'transform 200ms ease-out, opacity 200ms ease-out',
	},
	barHidden: {
		transform: 'translateY(100%)',
		opacity: 0,
		pointerEvents: 'none',
	},
	barSlideIn: {
		transform: 'translateY(0)',
		opacity: 1,
		pointerEvents: 'auto',
	},
	barNoTransition: {
		transition: 'none',
	},
	buttonStyle: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.raised'),
	},
	reactionsContainerBase: {
		padding: token('space.100'),
		overflow: 'visible',
	},
	commentsContainerBase: {
		padding: token('space.100'),
	},
	noPointerEvents: {
		pointerEvents: 'none',
	},
	pinContainerBase: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.full'),
		transition: 'opacity 200ms ease-out',
	},
	horizontalCenteringFixForToggleButton: {
		paddingLeft: token('space.500'),
	},
	pinButtonHiddenWhenNotHovered: {
		opacity: 0,
		pointerEvents: 'none',
	},
});

export const OmniBar: React.FC<OmniBarProps> = ({
	contentId,
	spaceId,
	contentType,
	contentSubType,
	onCommentClick,
}) => {
	const { formatMessage } = useIntl();
	const [isPinned, setIsPinned] = useState(true);
	const [isHovered, setIsHovered] = useState(false);
	const [shouldHideInstantly, setHideInstantly] = useState(false);
	const [isAnimatingOut, setIsAnimatingOut] = useState(false);
	const [hasActiveInteraction, setHasActiveInteraction] = useState(false);
	const [hasCompletedInitialSlideUp, setHasCompletedInitialSlideUp] = useState(false);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const isMountedRef = React.useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		const frameId = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (isMountedRef.current) {
					setHasCompletedInitialSlideUp(true);
				}
			});
		});
		return () => {
			isMountedRef.current = false;
			cancelAnimationFrame(frameId);
		};
	}, []);
	const handleOmniBarMouseEnter = useCallback(() => {
		if (isAnimatingOut) {
			return;
		}
		setHideInstantly(false);
		setIsHovered(true);
	}, [isAnimatingOut]);
	const handleOmniBarMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container || isPinned) return;

		const handleNativeMouseLeave = (e: MouseEvent) => {
			const relatedTarget = e.relatedTarget as HTMLElement | null;
			if (relatedTarget) {
				if (container.contains(relatedTarget)) return;
			}
			// Don't hide if a popup interaction is active (emoji picker, summary popup)
			if (hasActiveInteraction) return;
			setIsAnimatingOut(true);
			setIsHovered(false);
			setTimeout(() => {
				setIsAnimatingOut(false);
			}, 300);
		};

		container.addEventListener('mouseleave', handleNativeMouseLeave);
		return () => {
			container.removeEventListener('mouseleave', handleNativeMouseLeave);
		};
	}, [isPinned, hasActiveInteraction]);
	const shouldHideExternally = useShouldHideOmniBar();
	const { mode: rovoMode } = useRovoExperienceAPIStore();
	const isAIMiniModalOpen = rovoMode === 'mini-modal';
	const { handleCommentClick } = useOmnibarCommentTrigger(onCommentClick);

	const { showReactions, showComments } = useOmnibarEligibilityFromContext();

	const { reactionsData, isLoading: reactionsLoading } = useOmnibarReactionsData({
		contentId,
		spaceId,
		contentType,
		skip: !showReactions,
	});

	const pageContentDimensions = usePageContentDimensions();
	const minContentAreaWidth = 620;
	const isContentAreaAtMin =
		typeof pageContentDimensions.width === 'number' &&
		pageContentDimensions.width <= minContentAreaWidth;

	const widthStyle =
		typeof pageContentDimensions.width === 'number'
			? `${pageContentDimensions.width}px`
			: pageContentDimensions.width;

	const handlePinToggle = useCallback(() => {
		if (!isPinned) {
			setHideInstantly(true);
			setIsAnimatingOut(false);
			setIsPinned(true);
			requestAnimationFrame(() => {
				setHideInstantly(false);
			});
		} else {
			setIsAnimatingOut(true);
			setIsHovered(false);
			setHasActiveInteraction(false);
			setIsPinned(false);
			setTimeout(() => {
				setIsAnimatingOut(false);
			}, 200);
		}
	}, [isPinned]);

	const renderIcon = useCallback(
		(iconProps: React.ComponentProps<typeof ChevronDownIcon>) =>
			isPinned ? <ChevronDownIcon {...iconProps} /> : <ChevronUpIconRender {...iconProps} />,
		[isPinned],
	);

	if (isContentAreaAtMin || !showReactions) {
		return null;
	}

	return (
		<>
			<Box
				ref={containerRef}
				testId="omni-bar"
				data-vc="omni-bar-container"
				style={{ width: widthStyle }}
				xcss={cx(styles.responsiveContainer, shouldHideExternally && styles.noPointerEvents)}
				onMouseEnter={handleOmniBarMouseEnter}
				onFocus={handleOmniBarMouseEnter}
				onMouseLeave={handleOmniBarMouseLeave}
				onBlur={handleOmniBarMouseLeave}
			>
				<Inline
					space="space.100"
					alignBlock="center"
					xcss={cx(
						styles.barContainerBase,
						styles.horizontalCenteringFixForToggleButton,
						shouldHideInstantly && styles.barNoTransition,
						(!isPinned ||
							shouldHideExternally ||
							isAIMiniModalOpen ||
							(isPinned && !hasCompletedInitialSlideUp)) &&
							styles.barHidden,
						!shouldHideInstantly &&
							!shouldHideExternally &&
							!isAIMiniModalOpen &&
							((isPinned && hasCompletedInitialSlideUp) || isHovered || hasActiveInteraction) &&
							styles.barSlideIn,
					)}
				>
					{showReactions && (
						<Box xcss={cx(styles.reactionsContainerBase, styles.buttonStyle)}>
							<OmniBarReactions
								contentId={contentId}
								spaceId={spaceId}
								contentType={contentType}
								contentSubType={contentSubType}
								reactionsData={reactionsData}
								isLoading={reactionsLoading}
								shouldCloseInteractions={
									shouldHideExternally ||
									isAIMiniModalOpen ||
									(!isPinned && !isHovered && !hasActiveInteraction)
								}
								shouldShowBloom={
									!shouldHideExternally &&
									!isAIMiniModalOpen &&
									((isPinned && hasCompletedInitialSlideUp) || isHovered || hasActiveInteraction)
								}
								onInteractionStateChange={setHasActiveInteraction}
							/>
						</Box>
					)}
					{showComments && showReactions && (
						<Box xcss={cx(styles.commentsContainerBase, styles.buttonStyle)}>
							<Tooltip position="top" content={formatMessage(i18n.comment)} hideTooltipOnClick>
								<Button
									appearance="subtle"
									onClick={handleCommentClick}
									iconBefore={CommentAddIcon}
								>
									{formatMessage(i18n.comment)}
								</Button>
							</Tooltip>
						</Box>
					)}
					<Box
						xcss={cx(
							styles.pinContainerBase,
							isPinned && !isHovered && styles.pinButtonHiddenWhenNotHovered,
						)}
					>
						<Tooltip
							position="top"
							content={isPinned ? formatMessage(i18n.hideOmniBar) : formatMessage(i18n.pinOmniBar)}
							hideTooltipOnClick
						>
							<IconButton
								appearance="subtle"
								shape="circle"
								icon={renderIcon}
								label={isPinned ? formatMessage(i18n.hideOmniBar) : formatMessage(i18n.pinOmniBar)}
								onClick={handlePinToggle}
							/>
						</Tooltip>
					</Box>
				</Inline>
			</Box>
			<ExperienceSuccess name={VIEW_OMNIBAR_EXPERIENCE} />
		</>
	);
};
